/**
 * Demand capture — back-in-stock and coverage-gap sign-ups (§7, §8).
 *
 * Capture-only: nothing here promises an automated notification. Two paths:
 *
 *   endpoint set  → POST { email, reason, product, car, detail, consent } to the
 *                   Worker (full context → control-panel lists), then show
 *                   success inline without leaving the page. `detail` is the
 *                   free-text car or version the shopper typed, on the states
 *                   that ask for one.
 *   endpoint unset → let the native Shopify `customer` form submit via fetch,
 *                   creating a tagged customer the client can email manually.
 *
 * Either way the customer stays on the page — no reload, no lost cart.
 */
(() => {
  'use strict';

  /** Honour the OS setting — the same guard airflux-fitment.js uses. */
  const reduceMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

  /* The push is gated on analytics consent — see the long note on `track` in
     `airflux-fitment.js` for why the DOM half is not. */
  const track = (name, detail) => {
    document.dispatchEvent(new CustomEvent(`airflux:${name}`, { detail, bubbles: true }));
    if (window.AirfluxStorage?.may('analytics') && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: `airflux_${name}`, ...detail });
    }
  };

  class AirfluxCapture extends HTMLElement {
    connectedCallback() {
      this.endpoint = (this.dataset.endpoint || '').trim();
      this.reason = this.dataset.reason || 'oos';
      this.product = this.dataset.product || '';
      this.car = this.dataset.car || '';
      // Aligns the Shopify tag with the key the control panel counts under, so
      // the panel's "who asked for this car" link resolves. See carKeyOf().
      this.carKey = this.dataset.carKey || '';
      this.form = this.querySelector('[data-airflux-capture-form]');
      this.done = this.querySelector('[data-airflux-capture-done]');
      this.error = this.querySelector('[data-airflux-capture-error]');
      // The free-text answer, on the states that ask one. Absent everywhere
      // else — the out-of-stock form knows exactly which product it is for.
      this.ask = this.querySelector('[data-airflux-capture-ask-input]');
      // Which product line they were asking about, where they said so. Only
      // ever 'deflectors' or 'wipers' — it comes from the page they were on or
      // the button they pressed, never from anything typed, so it is safe to
      // become a Shopify tag. Blank on the unscoped finder, which expresses no
      // preference between the two and must not be recorded as expressing one.
      this.want = this.dataset.want || '';
      // 🔴 What the widget already worked out about this car, carried so the
      // demand row can be opened correctly for a shopper `/resolve` never saw.
      // That endpoint is gated on analytics consent and this one is not, so on
      // a refused (or unanswered) cookie banner THIS form is the only thing
      // that ever describes the car to the Worker — and without these two it
      // described it as a slug we stock nothing for. Same journey as `carKey`:
      // set by the state that knows, never invented here.
      this.coverage = this.dataset.coverage || '';
      this.stocks = this.dataset.stocks || '';

      this.form?.addEventListener('submit', (e) => this.onSubmit(e));
    }

    async onSubmit(e) {
      const emailInput = this.form.querySelector('input[type="email"]');
      const consent = this.form.querySelector('input[name="contact[accepts_marketing]"]');
      const email = emailInput?.value.trim();
      // Collapsed here as well as on the server: a pasted line break would be
      // stripped by the Worker anyway, and doing it before the fallback path
      // reads it keeps the two routes carrying the same string.
      const detail = this.ask?.value.replace(/\s+/g, ' ').trim() || '';

      /* Invalid — let the browser show its own validation and stop.

         🔴 The marketing tick is NOT part of this test any more, and must not
         become part of it again. Requiring it made the stock alert conditional
         on accepting marketing, which UK GDPR Article 7(4) forbids; the address
         alone is the request. An unticked box now means "tell me about this
         one thing and nothing else", which is a perfectly good sign-up. */
      if (!email) {
        e.preventDefault();
        this.form.reportValidity?.();
        return;
      }

      // ⚠️ Shopify decides on its own when a customer form needs bot
      // protection, and injects the challenge INTO the form — which on a long
      // product page can be well below the fold. A shopper who presses the
      // button, sees nothing happen and scrolls away has not refused the
      // challenge; they never knew it was there. Cheap and inert when there is
      // none, which is most of the time.
      this.querySelector('.h-captcha, .shopify-challenge__container, [data-shopify="captcha"]')
        ?.scrollIntoView({ block: 'center', behavior: reduceMotion() ? 'instant' : 'smooth' });

      // No Worker endpoint → let the native Shopify `customer` form submit
      // normally (full page). Shopify 403s a fetch POST to /contact, so this is
      // the only reliable no-backend path: it creates a tagged customer the
      // client can email manually (§8), and Shopify redirects back with the
      // form state. Nothing to intercept.
      //
      // ⚠️ The free-text answer cannot travel this way, and is not smuggled
      // through. `contact[tags]` is the only free-text channel the customer
      // form has, and a tag slugged from a sentence forks — "Ferrari 458" and
      // "ferrari 458 italia 2015" would be two tags holding one person each.
      // That is why the question is not asked at all when no endpoint is set;
      // see `mountCaptureForm`, which reveals the field only where the answer
      // has somewhere to be recorded.
      if (!this.endpoint) return;

      // Worker endpoint present → capture with full context, inline, no reload.
      e.preventDefault();
      // 🔴 …and stop the event dead, because `preventDefault` alone does not.
      //
      // Shopify attaches its own bot protection to `{% form 'customer' %}`, and
      // where the shop has it enabled the form carries an `h-captcha` element
      // and a listener of Shopify's own. That listener runs its challenge
      // ASYNCHRONOUSLY and then calls `form.submit()` when it resolves — the
      // native method, which fires no submit event and so cannot be prevented
      // by anything we do here.
      //
      // Reported live on iOS Safari: our fetch succeeded, the confirmation
      // appeared, and roughly ten seconds later the page reloaded on its own.
      // The customer record proves both writes happened — `airflux-coverage_gap`
      // from Shopify's own post alongside our `car:` and `want:` tags. Shopify
      // then reports `posted_successfully?` to EVERY customer form on the
      // reloaded page, so the footer newsletter thanked somebody for
      // subscribing who had asked about a car.
      //
      // Stopping propagation means Shopify's handler never sees the submit, so
      // the challenge never starts and there is no late submission to race.
      e.stopImmediatePropagation();
      const button = this.form.querySelector('[data-airflux-capture-submit]');
      button.disabled = true;
      const original = button.textContent;
      button.textContent = '…';
      this.error.hidden = true;

      let ok = false;
      try {
        const res = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            consent: !!consent?.checked,
            reason: this.reason,
            product: this.product,
            car: this.car,
            carKey: this.carKey,
            coverage: this.coverage,
            stocks: this.stocks,
            detail,
            want: this.want,
            shop: window.Shopify?.shop,
            at: new Date().toISOString(),
          }),
        });
        ok = res.ok;
      } catch {
        ok = false;
      }

      if (ok) {
        // 🔴 REMOVED, not hidden. A hidden form is still a form: anything
        // holding a reference to it — Shopify's captcha, an extension, a
        // stale timer — can still call `form.submit()` and navigate the page
        // out from under a shopper who has already been told they are done.
        // Detached, there is nothing left to submit. Only on success, because
        // a failure has to leave them something to try again with.
        this.form.remove();
        this.done.hidden = false;
        // 🔴 The surface AROUND the form has to know too. The question that
        // prompted this is still on screen — "Tell us which Lexus UX you have"
        // — and leaving it above a confirmation reads as a form that did not
        // submit. The class is what the gap panel's CSS hides the stale
        // question with; doing it declaratively means every surface that
        // mounts this form gets the behaviour without knowing about it.
        this.classList.add('is-done');
        // And bring the confirmation into view. The form can sit below the fold
        // on a long product page, and after Shopify's captcha has taken over
        // the screen and handed it back there is no telling where the reader is
        // looking. `nearest` so a form already on screen does not jump.
        this.scrollIntoView({ block: 'nearest', behavior: reduceMotion() ? 'instant' : 'smooth' });
        track('back_in_stock_signup', {
          reason: this.reason,
          product: this.product,
          car: this.car,
          // Whether they answered, never what they answered — this goes to the
          // dataLayer, and the customer's own words belong in Shopify.
          answered: !!detail,
        });
      } else {
        this.error.hidden = false;
        button.disabled = false;
        button.textContent = original;
      }
    }
  }

  if (!customElements.get('airflux-capture')) customElements.define('airflux-capture', AirfluxCapture);
})();
